import type React from 'react';
import { useState } from 'react';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import { interactionsName } from '@services/utils.service';
import SlideImage from '@components/Presentation/main/Sidebar/SlideImage';
import { type InteractionType } from '@components/Presentation/types';

interface Comment {
  body: string;
}

interface Interaction {
  interaction: InteractionType;
  comments: Comment[];
  feature_id?: string;
}

interface WheelCarouselProps {
  comments?: Interaction[];
  textColor: string;
  currentRosFeature?: Record<string, any>;
  className?: string;
  height?: string;
  width?: string;
}

const CAROUSEL_STYLES = {
  container: 'relative rounded-md shadow-md flex overflow-hidden bg-[rgb(50,50,50,0.1)] h-fit',
  navigationButton: 'bg-[rgb(50,50,50,0.3)] w-16 shrink-0',
  navigationButtonLeft: 'rounded-l-xl',
  navigationButtonRight: 'rounded-r-xl',
  header: 'flex items-center justify-center text-2xl py-2 gap-2 bg-[rgb(50,50,50,0.2)]',
  content: 'flex items-center',
  commentsContainer: 'text-left h-[17vh] overflow-scroll w-full overflow-x-hidden',
  commentItem: 'flex items-center px-2 text-2xl w-[460px] overflow-hidden',
  divider: 'border-t border-2 border-[rgb(50,50,50,0.2)]',
} as const;

const DEFAULT_DIMENSIONS = {
  height: 'min-h-[229px] h-full max-h-[400px]',
  width: 'w-full max-w-[612.5px]',
} as const;

const NavigationButton: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
  disabled: boolean;
  textColor: string;
  interactionCount?: number;
}> = ({ direction, onClick, disabled, textColor, interactionCount }) => {
  const Icon = direction === 'left' ? ChevronLeftRoundedIcon : ChevronRightRoundedIcon;
  const buttonClass = `${CAROUSEL_STYLES.navigationButton} ${
    direction === 'left' ? CAROUSEL_STYLES.navigationButtonLeft : CAROUSEL_STYLES.navigationButtonRight
  }${interactionCount === 1 ? ' hidden' : ''}`;

  return (
    <button
      onClick={onClick}
      title={`${direction === 'left' ? 'Previous' : 'Next'} Slide`}
      className={buttonClass}
      disabled={disabled}
    >
      <Icon style={{ fontSize: '62px', color: textColor, opacity: disabled ? 0 : 1 }} />
    </button>
  );
};

const CommentItem: React.FC<{ comment: Comment; textColor: string; showDivider: boolean }> = ({
  comment,
  textColor,
  showDivider,
}) => (
  <>
    <div className={CAROUSEL_STYLES.commentItem} style={{ color: textColor }}>
      <p className="line-clamp-2 overflow-hidden text-ellipsis py-1"
        style={{ color: textColor }}>
        {comment?.body}
      </p>
    </div>
    {showDivider && <hr className={CAROUSEL_STYLES.divider} />}
  </>
);
const CarouselHeader: React.FC<{ interaction: keyof typeof interactionsName; textColor: string; interactionType: keyof typeof interactionsName }> = ({ interaction, textColor, interactionType }) => {
  const interactionName = interactionsName[interactionType] || interactionsName[interaction] || '';

  return (
    <div className={CAROUSEL_STYLES.header} style={{ color: textColor }}>
      <SlideImage slideType={interaction as InteractionType} svgHeight={24} svgWidth={24} color={textColor}/>
      <p>{interactionName.name}</p>
    </div>
  );
};

const WheelCarousel: React.FC<WheelCarouselProps> = ({
  textColor,
  comments = [],
  className = '',
  currentRosFeature
}) => {
  const [index, setIndex] = useState(0);

  const prevSlide = () => {
    if (index === 0) return;
    setIndex((prev) => (prev === 0 ? comments.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    if (index + 1 === comments.length) return;
    setIndex((prev) => (prev === comments.length - 1 ? 0 : prev + 1));
  };

  const currentInteraction = comments[index] && comments[index].feature_id ? comments[index] : null;
  const isPrevDisabled = comments.length === 0 || index === 0 || currentInteraction === null;
  const isNextDisabled = comments.length === 0 || index + 1 === comments.length || currentInteraction === null;

  const containerClasses = [
    CAROUSEL_STYLES.container,
    DEFAULT_DIMENSIONS.height,
    DEFAULT_DIMENSIONS.width,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <NavigationButton
        direction="left"
        onClick={prevSlide}
        disabled={isPrevDisabled}
        textColor={textColor}
        interactionCount={comments?.length}
      />

      <div className="flex-1 flex flex-col">
        {currentInteraction && (
          <>
            <CarouselHeader
              textColor={textColor}
              interactionType= { currentRosFeature?.metadata?.featureType}
              interaction={currentInteraction.interaction as any}/>
            <div className={CAROUSEL_STYLES.content}>
              <div className={CAROUSEL_STYLES.commentsContainer}>
                {currentInteraction.comments?.map((comment, idx) => (
                  <CommentItem
                    key={idx}
                    comment={comment}
                    textColor={textColor}
                    showDivider={idx < currentInteraction.comments.length - 1}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <NavigationButton
        direction="right"
        onClick={nextSlide}
        disabled={isNextDisabled}
        textColor={textColor}
        interactionCount={comments?.length}
      />
    </div>
  );
};

export default WheelCarousel;
